import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/common/PhoneField";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ACQUISITION_SOURCES } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, Gift } from "lucide-react";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").trim().toUpperCase();
  const invited = !!refCode;
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "", password: "", birthdate: "",
    acquisition_source: invited ? "amica" : "", referrer_name_raw: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (invited) setF((s) => ({ ...s, acquisition_source: "amica" }));
  }, [invited]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.acquisition_source) { toast.error("Seleziona come ci hai conosciuti"); return; }
    setLoading(true);
    try {
      await register({ ...f, ref: refCode || undefined });
      toast.success("Account creato");
      navigate("/portale");
    } catch (err) { toast.error(err?.response?.data?.detail || "Registrazione non riuscita"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 hi-app-bg">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6"><div className="hi-plaque rounded-xl overflow-hidden" style={{ width: 64, height: 64, padding: 3 }}><img src="/logo.jpg" className="w-full h-full object-contain" alt="Hair Identity" /></div></div>
        <h1 className="font-heading text-3xl font-semibold text-center">Crea il tuo account</h1>
        <p className="text-sm text-muted-foreground text-center mt-1 mb-6">Prenota online da Hair Identity</p>

        {invited && (
          <div className="mb-5 flex items-center gap-2 rounded-lg border border-[hsl(var(--hi-gold)/0.5)] bg-[hsl(var(--hi-gold)/0.08)] p-3 text-sm" data-testid="reg-invited-banner">
            <Gift className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
            <span>Ti ha invitata un'amica! Avrai <b>-10%</b> sul tuo primo servizio.</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nome</Label><Input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} required data-testid="reg-first-name" /></div>
            <div className="space-y-1.5"><Label>Cognome</Label><Input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="reg-last-name" /></div>
          </div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="reg-email" /></div>
          <div className="space-y-1.5"><Label>Telefono</Label><PhoneField value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="reg-phone" /></div>
          <div className="space-y-1.5"><Label>Data di nascita <span className="text-muted-foreground font-normal">(facoltativa)</span></Label><Input type="date" value={f.birthdate} onChange={(e) => setF({ ...f, birthdate: e.target.value })} data-testid="reg-birthdate" /></div>

          {!invited && (
            <div className="space-y-1.5">
              <Label>Come ci hai conosciuti? <span className="text-[hsl(var(--hi-status-danger))]">*</span></Label>
              <Select value={f.acquisition_source} onValueChange={(v) => setF({ ...f, acquisition_source: v })}>
                <SelectTrigger data-testid="reg-acquisition-source"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  {ACQUISITION_SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!invited && f.acquisition_source === "amica" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Come si chiama l'amica che ti ha presentata?</Label>
              <Input value={f.referrer_name_raw} onChange={(e) => setF({ ...f, referrer_name_raw: e.target.value })} placeholder="Nome e cognome amica" data-testid="reg-referrer-name" />
            </div>
          )}

          <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="reg-password" /></div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="reg-submit">{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Crea account</Button>
        </form>
        <div className="flex items-center gap-3 my-4">
          <span className="h-px flex-1 bg-border" />
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">oppure</span>
          <span className="h-px flex-1 bg-border" />
        </div>
        <GoogleSignInButton testid="register-google-btn" />
        <p className="text-sm text-center text-muted-foreground mt-4">Hai già un account? <Link to="/login" className="text-[hsl(var(--hi-gold))] font-medium">Accedi</Link></p>
      </div>
    </div>
  );
}
